import * as React from 'react';
import {
  cn,
  sortViewsByDisplayOrder,
  sortFoldersByDisplayOrder
} from '../../../utils';
import { Add } from '../../Icon/icon';
import { Icon } from 'rcd-cpp-ui/ui/icon';
import { useActions as useViewModalActions } from '../../ViewModal/viewModalActions';
import { Dropdown } from '../../Dropdown/dropdown';
import { View } from '../../../types/View';
import { ReactNode } from 'react';
import { useDrop } from 'react-dnd';
import { useActions as useNavActions } from '../navActions';
import { useActions as useViewActions } from '../../View/viewActions';
import { Loader } from '../../Loader/loader';
import { Alert } from '../../Alert/alert';
import { ConfirmModal } from '../../ConfirmModal/confirmModal';
import { NodeType } from '../../../enums/NodeType';
import { NavGroupHeader } from '../NavGroup/navGroup';
import { NavGroups } from '../../../enums/NavGroups';
import { useSelector } from '../../../utils/useSelector';
import { Workspace } from '../../../types';
import { ViewFolder } from './navViewFolder';
import { ViewItem } from './navViewItem';
import { NavViewOptionName } from '../../../enums/NavViewOptionName';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updatePlanningState } from '../../ContentPlanning/contentPlanningReducer';

type Option = { name: string; Icon: any };
interface NavViewProps {
  disabled?: boolean;
  greedy?: boolean;
  children?: ReactNode;
}

export const NavView = ({ disabled, greedy }: NavViewProps) => {
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);
  const selectedWorkspaceRefId = useSelector(
    s => s.planning?.selectedWorkspace?.props?.refId
  );
  const sortedViews = sortViewsByDisplayOrder(selectedWorkspace?.views);
  const sortedFolders = sortFoldersByDisplayOrder(selectedWorkspace?.folders);
  const previewFolders =
    useSelector(s => s.planning?.previewFolders) || sortedFolders;
  const previewRootViews =
    useSelector(s => s.planning?.previewRootViews) || sortedViews;
  const selectedViewId = useSelector(s => s.view?.selected?.id);
  const [isExpanded, setExpanded] = React.useState(true);
  const [folderName, setFolderName] = React.useState('');
  const [showFolderModal, setShowFolderModal] = React.useState(null);
  const [folderToDelete, setFolderToDelete] = React.useState(null);
  const [viewToDelete, setViewToDelete] = React.useState<View.Setting>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { openViewModal, deleteView, cloneView, cloneFolder } =
    useViewModalActions();
  const { setViewSettings } = useViewActions();
  const {
    navigate,
    save,
    deleteFolder,
    cleanUpFolderModalAlerts,
    resetEditMode
  } = useNavActions();
  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(
      updatePlanningState({
        previewFolders: sortFoldersByDisplayOrder(selectedWorkspace?.folders),
        previewRootViews: sortViewsByDisplayOrder(selectedWorkspace?.views)
      })
    );
  }, [selectedWorkspace?.views, selectedWorkspace?.folders]);

  const handleMoveView = (viewRefId: string, toId: string, toIndex: number) => {
    const toFolderId = toId == selectedWorkspaceRefId ? null : toId;
    let movedView: View.Setting;
    let newFolders = previewFolders;
    let newRootViews = previewRootViews;

    if (!toFolderId) {
      newFolders = previewFolders.map(folder => {
        const idx = (folder?.views ?? []).findIndex(
          v => v.props.refId == viewRefId
        );
        if (idx !== -1) {
          movedView = folder?.views[idx];
          const newViews = [...(folder?.views ?? [])];
          newViews.splice(idx, 1);
          return { ...folder, views: newViews };
        }
        return folder;
      });
      newRootViews = previewRootViews.filter(v => v.props.refId !== viewRefId);
      if (!movedView) {
        movedView = previewRootViews.find(v => v.props.refId == viewRefId);
      }
      if (movedView) {
        const insertAt = Math.max(0, Math.min(toIndex, newRootViews.length));
        newRootViews = [
          ...newRootViews.slice(0, insertAt),
          movedView,
          ...newRootViews.slice(insertAt)
        ];
      }
    } else {
      newRootViews = previewRootViews.filter(v => v.props.refId !== viewRefId);
      let foundView: View.Setting = movedView;
      newFolders = previewFolders.map(folder => {
        const idx = folder.views.findIndex(v => v.props.refId == viewRefId);
        if (idx != -1) {
          foundView = folder.views[idx];
          const newViews = [...folder.views];
          newViews.splice(idx, 1);
          return { ...folder, views: newViews };
        }
        return folder;
      });
      if (!foundView) {
        foundView = previewRootViews.find(v => v.props.refId == viewRefId);
      }
      if (foundView) {
        const folderIdx = newFolders.findIndex(
          f => f.props.refId == toFolderId
        );
        if (folderIdx != -1) {
          const folder = newFolders[folderIdx];
          const newViews = [...folder.views];
          newViews.splice(toIndex, 0, foundView);
          newFolders[folderIdx] = { ...folder, views: newViews };
        }
      }
    }
    dispatch(
      updatePlanningState({
        previewFolders: newFolders,
        previewRootViews: newRootViews
      })
    );
  };

  const handleSelectView = (v: View.Setting) => {
    resetEditMode();
    navigate(selectedWorkspace, { v: v.props.refId });
  };
  const handleSelectOption = (
    o: Option,
    item?: View.Setting | View.Folder,
    folderRefId?: string
  ) => {
    if (o.name == NavViewOptionName.AddView) openViewModal();
    if (isView(item)) {
      if (o.name == NavViewOptionName.Edit) openViewModal(item);
      if (o.name == NavViewOptionName.Copy)
        cloneView(item, folderRefId, setSearchParams);
      if (o.name == NavViewOptionName.Delete) setViewToDelete(item);
      if (o.name == NavViewOptionName.Favorite) {
        if (item?.props?.refId) {
          setViewSettings(
            {
              isFavorited: true
            },
            item.props.refId
          );
        }
      }
      if (o.name == NavViewOptionName.Unfavorite) {
        if (item?.props?.refId) {
          setViewSettings(
            {
              isFavorited: false
            },
            item.props.refId
          );
        }
      }
    } else {
      if (o.name == NavViewOptionName.AddFolder) setShowFolderModal({});
      if (o.name == NavViewOptionName.AddView) openViewModal(null, item);
      if (o.name == NavViewOptionName.Copy) {
        cloneFolder(item?.props?.refId);
      }
      if (o.name == NavViewOptionName.Rename) {
        setShowFolderModal(item);
        setFolderName(item?.props?.name);
      }
      if (o.name == NavViewOptionName.Delete) setFolderToDelete(item);
    }
  };

  const { onDrop } = useNavActions();

  const [{ isOver, isOverCurrent }, viewDrop]: any = useViewDrop({
    isSystemFolder: false,
    greedy,
    onDropHandler: () => {
      onDrop();
    },
    onHover: null
  });

  const handleMoveFolder = (from: number, to: number) => {
    const newFolders = [...previewFolders];
    const [removed] = newFolders.splice(from, 1);
    newFolders.splice(to, 0, removed);
    dispatch(
      updatePlanningState({
        previewFolders: newFolders,
        previewRootViews
      })
    );
  };

  return (
    <>
      <div className={cn({ 'nav-group-collasped': !isExpanded }, 'nav-group')}>
        <NavGroupHeader
          name={NavGroups.Views}
          isExpanded={!!isExpanded}
          toggleIsExpanded={() => {
            setExpanded(!isExpanded);
          }}
        >
          <span>
            {!disabled && (
              <Dropdown
                options={[
                  { name: NavViewOptionName.AddView, Icon: Add },
                  { name: NavViewOptionName.AddFolder, Icon: Add }
                ]}
                position="right"
                onSelect={handleSelectOption}
                renderOption={(o: Option) => {
                  const I = o.Icon;
                  return (
                    <div>
                      <span>{I && <I />}</span>
                      <span className="dd-text" data-txt={o.name}>
                        {o.name}
                      </span>
                    </div>
                  );
                }}
              >
                <Icon>
                  <Add />
                </Icon>
              </Dropdown>
            )}
          </span>
        </NavGroupHeader>
        <div ref={viewDrop} className="nav-views">
          <Loader id="navDragAndDrop" spinnerSize="small" />
          <div>
            {previewFolders
              ?.filter(f => f && f.props)
              .map((f, i) => {
                return (
                  <ViewFolder
                    isExpanded={isExpanded}
                    disabled={disabled}
                    handleSelectOption={handleSelectOption}
                    handleSelectView={handleSelectView}
                    selectedItemId={selectedViewId}
                    f={f}
                    key={f.props.refId}
                    onMoveView={handleMoveView}
                    index={i}
                    onMoveFolder={handleMoveFolder}
                  />
                );
              })}
            {!!isExpanded &&
              previewRootViews?.map((v, i) => {
                return (
                  <ViewItem
                    disabled={disabled}
                    handleSelectOption={handleSelectOption}
                    handleSelectView={handleSelectView}
                    selectedItemId={selectedViewId}
                    v={v}
                    key={v.props.refId}
                    inFavoritedFolder={false}
                    index={i}
                    parentRefId={selectedWorkspaceRefId}
                    onMoveView={handleMoveView}
                  />
                );
              })}
          </div>
        </div>
      </div>
      {showFolderModal && (
        <ConfirmModal
          header={
            showFolderModal?.props?.refId.length
              ? 'Rename Folder'
              : 'Create Folder'
          }
          onCancel={() => {
            setShowFolderModal(null);
            setFolderName('');
            cleanUpFolderModalAlerts();
          }}
          onCreate={() => {
            save(
              folderName,
              () => {
                setShowFolderModal(false);
                setFolderName('');
                cleanUpFolderModalAlerts();
              },
              showFolderModal
            );
          }}
        >
          <Loader id="folderModalLoader" />
          <Alert id="folderModalAlert" />
          <label htmlFor="viewName">Folder Name</label>
          <div className="input-group">
            <input
              id="folderName"
              type="text"
              className="form-control"
              value={folderName}
              onChange={e => {
                setFolderName(e.target.value);
              }}
            />
          </div>
        </ConfirmModal>
      )}
      {folderToDelete != null && (
        <ConfirmModal
          header="Delete Folder"
          onCancel={() => setFolderToDelete(null)}
          onDelete={() => {
            deleteFolder(folderToDelete?.props?.refId);
            setFolderToDelete(null);
          }}
        >
          <div>
            Are you sure you want to delete <b>{folderToDelete.props?.name}</b>?
            All views within the folder will be deleted.
          </div>
        </ConfirmModal>
      )}
      {viewToDelete && (
        <DeleteViewModal
          view={viewToDelete}
          onCancel={() => setViewToDelete(null)}
          onDelete={() => {
            setViewToDelete(null);
            deleteView(viewToDelete);
          }}
        />
      )}
    </>
  );
};

type DVMProps = {
  view: View.Setting;
  onCancel: () => any;
  onDelete: () => any;
};
const DeleteViewModal = ({ view, onCancel, onDelete }: DVMProps) => {
  const getAPI = useSelector(s => s.app?.elFn?.getAPI);
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);
  const [loading, setLoading] = React.useState(true);
  const [inheritedInWorkspaces, setInheritedInWorkspaces] =
    React.useState<Workspace[]>(null);
  React.useEffect(() => {
    if (selectedWorkspace?.childrenRefIds?.length) {
      setLoading(true);
      const { getViewInfo } = getAPI();
      getViewInfo(view.props.refId)
        .then(r => {
          setInheritedInWorkspaces(r.inheritedByWorkspaces);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [selectedWorkspace, view, getAPI]);
  if (view?.parent != null) {
    return (
      <ConfirmModal header="Delete View" onCancel={onCancel}>
        <div>
          This is an inherited View from a parent Workspace, you cannot delete
          this view.
        </div>
        <div>
          View Name: <b>{view.props?.name}</b>
        </div>
      </ConfirmModal>
    );
  }
  return (
    <ConfirmModal
      header="Delete View"
      onCancel={onCancel}
      onDelete={onDelete}
      disabled={loading}
    >
      <div>Are you sure you want to delete this view?</div>
      <div>
        View Name: <b>{view.props?.name}</b>
      </div>
      <InheritedList
        selectedWorkspace={selectedWorkspace}
        inheritedInWorkspaces={inheritedInWorkspaces}
      />
    </ConfirmModal>
  );
};

const InheritedList = ({
  selectedWorkspace,
  inheritedInWorkspaces
}: {
  selectedWorkspace: Workspace;
  inheritedInWorkspaces: Workspace[];
}) => {
  if (!inheritedInWorkspaces?.length) return null;
  if (selectedWorkspace?.labels?.includes(NodeType.AdminWorkspace)) {
    return (
      <div className="inherited-list">
        This is inherited to the following Workspaces: <b>All Workspaces</b>
      </div>
    );
  }
  return (
    <div className="inherited-list">
      This is inherited to the following Workspaces:{' '}
      {inheritedInWorkspaces.map((n, i) => (
        <>
          {i > 0 ? ', ' : ''}
          <b>{n.props.name}</b>
        </>
      ))}
    </div>
  );
};

const isView = (i: any): i is View.Setting => {
  return i?.labels?.includes(NodeType.View);
};

export const useViewDrop = ({
  isSystemFolder,
  greedy,
  onDropHandler,
  onHover
}) => {
  return isSystemFolder
    ? [{ isOver: false, isOverCurrent: false }, null]
    : useDrop(
        () => ({
          accept: 'View',
          drop(_item: unknown, monitor) {
            if (!monitor.isOver({ shallow: true })) return;
            if (onDropHandler) onDropHandler(_item);
          },
          hover(_item, monitor) {
            if (onHover) onHover(_item, monitor);
          },
          collect: monitor => ({
            isOver: monitor.isOver(),
            isOverCurrent: monitor.isOver({ shallow: true })
          })
        }),
        [greedy, onDropHandler, onHover]
      );
};
